import { ReactElement, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { ApiArtizenBO } from 'utils/artizenConnector'
import TableInfinite from 'components/table/infinite/InfiniteTable'
import { definitionFields, FacturXReadyTestResultColor } from './EinvoicingArtisansCtrl.definitionFields'
import {
  ArtizenUserClass,
  FacturXReadyTestResult,
  TEfactureArtisan,
  TEfactureArtisanSave,
} from '@Arti-zen/package-backoffice'
import { StatusSelect } from './EinvoicingArtisansCtrl.StatusSelect'
import SearchInputSync from 'components/forms/SearchInputSync'
import { Dialog } from '@mui/material'
import { interestLevelColors, interestLevelLabels } from './InterestLevelContent'

export class ArtisanWithEfacture extends ArtizenUserClass {
  interestLevel: string
  reason: string
  comment: string

  constructor(
    artisan: ArtizenUserClass,
    public efactureArtisan?: TEfactureArtisan
  ) {
    super(artisan)
    const interestLevelFulfilled =
      [FacturXReadyTestResult.PdpAccountReady, FacturXReadyTestResult.PdpAccountInReview, FacturXReadyTestResult.PdpAccountFailed].includes(
        artisan?.isFacturXReady?.state
      )
        ? 'PA' : efactureArtisan?.interestLevel || 'non contacté'

    this.interestLevel = interestLevelFulfilled
    this.reason = efactureArtisan?.reason
    this.comment = efactureArtisan?.comment
  }
}

const stateList = Object.values(FacturXReadyTestResult) as FacturXReadyTestResult[]

export default function EinvoicingArtisansCtrl(): ReactElement {
  const [artisanList, setArtisanList] = useState<ArtizenUserClass[]>([])
  const [efactureArtisanList, setEfactureArtisanList] = useState<TEfactureArtisan[]>([])
  const [filters, setFilters] = useState<FacturXReadyTestResult[]>(stateList)
  const [filterByInterestLevel, setFilterByInterestLevel] = useState<string[]>([
    ...Object.keys(interestLevelLabels),
    'non contacté',
  ])
  const [search, setSearch] = useState<string>('')
  const [dialogContent, setDialogContent] = useState<ReactElement | null>(null)

  useEffect(() => {
    ApiArtizenBO.artizenUsers
      .getCompanies()
      .then(setArtisanList)
      .catch(console.error)

    ApiArtizenBO.efactureArtisan.getAll().then(setEfactureArtisanList).catch(console.error)
  }, [])

  const artisanListWithEfacture: ArtisanWithEfacture[] = useMemo(() => {
    return artisanList.map((artisan) => {
      const efactureArtisan = efactureArtisanList.find((e) => e.companyId === artisan.id)
      return new ArtisanWithEfacture(artisan, efactureArtisan)
    })
  }, [artisanList, efactureArtisanList])

  const listFiltered = useMemo(() => {
    return artisanListWithEfacture
      .filter(({ isFacturXReady }) => filters.includes(isFacturXReady.state))
      .filter(
        ({ interestLevel }) =>
          (filterByInterestLevel.includes('non contacté') && !interestLevel) ||
          filterByInterestLevel.includes(interestLevel)
      )
      .filter(({ id, email, companyName }) => {
        if (!search) return true
        const searchLower = search.toLowerCase()
        return (
          String(id).toLowerCase().includes(searchLower) ||
          email?.toLowerCase().includes(searchLower) ||
          companyName?.toLowerCase().includes(searchLower)
        )
      })
  }, [artisanListWithEfacture, filters, filterByInterestLevel, search])

  const { interestResults } = useMemo(() => {
    const resultsRaw = artisanListWithEfacture.reduce(
      (acc, artisan) => {
        acc[artisan.interestLevel ?? 'non contacté'] = (acc[artisan.interestLevel ?? 'non contacté'] ?? 0) + 1
        return acc
      },
      {
        Intéressé: 0,
        'A relancer': 0,
        'Pas intéressé': 0,
        'non contacté': 0,
        'pas de réponse': 0,
        'PA': 0,
      } as Record<string, number>
    )

    const total = resultsRaw.Intéressé + resultsRaw['A relancer'] + resultsRaw['Pas intéressé'] + resultsRaw['non contacté']

    const interestResults = Object.fromEntries(
      Object.entries(resultsRaw).map(([state, nb]) => [state, `${nb} / ${((nb / total) * 100).toFixed(2)}%`])
    ) as Record<FacturXReadyTestResult, string>

    return { total, interestResults }
  }, [artisanListWithEfacture])

  const { total, results } = useMemo(() => {
    const total = artisanListWithEfacture.length
    const resultsRaw = artisanListWithEfacture.reduce(
      (acc, artisan) => {
        acc[artisan.isFacturXReady.state] = (acc[artisan.isFacturXReady.state] ?? 0) + 1
        return acc
      },
      {
        [FacturXReadyTestResult.MissingInfos]: 0,
        [FacturXReadyTestResult.PdpAccountToCreate]: 0,
        [FacturXReadyTestResult.PdpAccountInError]: 0,
        [FacturXReadyTestResult.PdpAccountReady]: 0,
        [FacturXReadyTestResult.PdpAccountInReview]: 0,
        [FacturXReadyTestResult.PdpAccountFailed]: 0,
      } as Record<FacturXReadyTestResult, number>
    )

    const results = Object.fromEntries(
      Object.entries(resultsRaw).map(([state, nb]) => [state, `${nb} / ${((nb / total) * 100).toFixed(2)}%`])
    ) as Record<FacturXReadyTestResult, string>

    return { total, results }
  }, [artisanListWithEfacture])

  const updateEfactureArtisanList = async (companyId: TEfactureArtisan['companyId'], changes: TEfactureArtisanSave) => {
    await ApiArtizenBO.efactureArtisan.putByCompany({ companyId, changes }).catch(console.error)
    ApiArtizenBO.efactureArtisan.getAll().then(setEfactureArtisanList).catch(console.error)
  }

  return (
    <Wrapper>
      <div style={{ flexDirection: 'row', display: 'flex', gap: 10, justifyContent: 'center', width: '100%' }}>
        <StatusSelect
          statusList={stateList}
          filters={filters}
          setFilters={setFilters}
          stats={results}
          colorByStatus={FacturXReadyTestResultColor}
        />

        <div style={{ color: 'black' }}>Total : {total}</div>
      </div>

      <StatusSelect
        statusList={['non contacté', ...Object.keys(interestLevelLabels)]}
        filters={filterByInterestLevel}
        setFilters={setFilterByInterestLevel}
        colorByStatus={interestLevelColors}
        stats={interestResults}
      />

      <div style={{ width: '50%' }}>
        <SearchInputSync onSubmit={setSearch} placeholder="Taper votre recherche, puis valider" displayCloseCross />
      </div>

      <TableInfinite<ArtisanWithEfacture>
        data={listFiltered}
        definitionFields={definitionFields({ setDialogContent, updateEfactureArtisanList })}
        nextPage={null}
        isQueriedAllCompleted={true}
      />
      <Dialog open={!!dialogContent} onClose={() => setDialogContent(null)} fullScreen={false}>
        {dialogContent || 'Erreur inconnue'}
      </Dialog>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`
